import { z } from 'zod';
// Reservation policy constants and schema
export const RESERVATION_POLICY = ['required', 'recommended', 'drop-ins-welcome'];
// Price range constants and schema
export const PRICE_RANGES = [1, 2, 3, 4, 5];
export const priceRangeSchema = z
    .number({
    invalid_type_error: 'Price range must be a number',
})
    .int('Price range must be a whole number')
    .min(1, 'Price range must be between 1 and 5')
    .max(5, 'Price range must be between 1 and 5')
    .optional();
// Primary atmosphere constants and schema
export const PRIMARY_ATMOSPHERES = ['casual', 'upscale', 'rustic', 'modern'];
export const primaryAtmosphereSchema = z
    .enum(PRIMARY_ATMOSPHERES, {
    errorMap: () => ({
        message: `Primary atmosphere must be one of: ${PRIMARY_ATMOSPHERES.join(', ')}`,
    }),
})
    .optional();
// Amenities constants and schema
export const AMENITIES = [
    'scenic-views',
    'historic-building',
    'family-friendly',
    'pet-friendly',
    'outdoor-seating',
    'live-music',
    'food-service',
    'tours-available',
    'event-space',
    'wheelchair-accessible',
];
export const amenitiesSchema = z
    .array(z.enum(AMENITIES, {
    errorMap: () => ({
        message: `Invalid amenity. Valid options are: ${AMENITIES.join(', ')}`,
    }),
}), {
    invalid_type_error: 'Amenities must be an array',
})
    .default([])
    .refine((amenities) => {
    const uniqueAmenities = new Set(amenities);
    return uniqueAmenities.size === amenities.length;
}, {
    message: 'Duplicate amenities are not allowed',
});
// Featured constants and schema
export const FEATURED_LEVELS = [0, 1, 2, 3, 4, 5];
export const featuredSchema = z
    .number({
    invalid_type_error: 'Featured must be a number',
})
    .int('Featured must be a whole number')
    .min(0, 'Featured must be between 0 and 5')
    .max(5, 'Featured must be between 0 and 5')
    .optional();
// Description source schema
export const DESCRIPTION_SOURCES_SCHEMA = ['manual', 'google_places', 'wikipedia', 'ai_generated'];
export const descriptionSourceSchema = z.enum(DESCRIPTION_SOURCES_SCHEMA).optional();
// Operating hours schema - used for both create and update
export const operatingHoursSchema = z
    .record(z.object({
    open: z.string().optional(),
    close: z.string().optional(),
    closed: z.boolean().optional(),
}))
    .optional();
// Social media schema
export const socialMediaSchema = z
    .object({
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    youtube: z.string().optional(),
    tiktok: z.string().optional(),
})
    .optional();
// Address schema
export const addressSchema = z.object({
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    country: z.string().default('USA'),
    coordinates: z
        .object({
        lat: z.number(),
        lng: z.number(),
    })
        .optional(),
});
// Venue create input schema
export const venueCreateInputSchema = z.object({
    name: z.string().min(1),
    venueType: z.enum(['winery', 'tasting room', 'wine bar', 'restaurant', 'other']).default('winery'),
    status: z.enum(['draft', 'published']).default('published'),
    description: z.string().optional(),
    address: addressSchema,
    phone: z.string().optional(),
    website: z.string().url().optional().or(z.literal('')),
    email: z.string().email().optional().or(z.literal('')),
    socialMedia: socialMediaSchema,
    logoImageId: z.string().optional(),
    heroImageIds: z.array(z.string()).max(5, 'Maximum 5 hero images allowed').default([]),
    imageIds: z.array(z.string()).default([]),
    operatingHours: operatingHoursSchema,
    operatingHoursNotes: z.string().optional(),
    foodAvailableForPurchase: z.boolean().optional(),
    foodDescription: z.string().optional(),
    priceRange: priceRangeSchema,
    primaryAtmosphere: primaryAtmosphereSchema,
    amenities: amenitiesSchema,
    featured: featuredSchema,
    operatingHoursValidated: z.boolean().optional(),
    descriptionSource: descriptionSourceSchema,
    descriptionSourceRef: z.string().optional(),
    reservationPolicy: z.enum(RESERVATION_POLICY).optional(),
    bookingUrl: z
        .string()
        .url()
        .optional()
        .or(z.literal(''))
        .transform((v) => (v === '' ? undefined : v)),
});
// Venue update input schema
export const venueUpdateInputSchema = z.object({
    id: z.string(),
    name: z.string().min(1).optional(),
    venueType: z.enum(['winery', 'tasting room', 'wine bar', 'restaurant', 'other']).optional(),
    status: z.enum(['draft', 'published']).optional(),
    description: z.string().optional(),
    address: addressSchema.optional(),
    phone: z.string().optional(),
    website: z.string().url().optional().or(z.literal('')),
    email: z.string().email().optional().or(z.literal('')),
    socialMedia: socialMediaSchema,
    logoImageId: z.string().optional(),
    heroImageIds: z.array(z.string()).max(5, 'Maximum 5 hero images allowed').optional(),
    imageIds: z.array(z.string()).optional(),
    operatingHours: operatingHoursSchema,
    operatingHoursNotes: z.string().optional(),
    foodAvailableForPurchase: z.boolean().optional(),
    foodDescription: z.string().optional(),
    priceRange: priceRangeSchema,
    primaryAtmosphere: primaryAtmosphereSchema,
    amenities: amenitiesSchema.optional(),
    featured: featuredSchema,
    operatingHoursValidated: z.boolean().optional(),
    descriptionSource: descriptionSourceSchema,
    descriptionSourceRef: z.string().optional(),
    reservationPolicy: z.enum(RESERVATION_POLICY).optional(),
    bookingUrl: z
        .string()
        .url()
        .optional()
        .or(z.literal(''))
        .transform((v) => (v === '' ? undefined : v)),
});
